import { useCallback, useEffect, useRef, useState } from "react";
import socket from "../socket";
import { useFarcaster } from "../contexts/FarcasterContext";
import CONFIG from "../config";

interface UseVoiceChatReturn {
  isVoiceEnabled: boolean;
  isMuted: boolean;
  activePeers: string[];
  mutedPeers: string[];
  toggleVoice: () => void;
  toggleMute: () => void;
  error: string | null;
}

/**
 * Manages WebRTC peer connections for in-room voice chat.
 * Uses Socket.IO as the signaling channel.
 *
 * Fixes applied:
 *   - H6: Politeness captured once per PC and stored; glare handling uses it.
 *   - H7: ICE candidates are queued until `setRemoteDescription` completes.
 *   - H8: Peer connections are torn down when the peer's player disconnects.
 *   - H9: Muting no longer tears down the peer connection — a new
 *         `voice-mute-state` signal tells peers to show a muted indicator
 *         without destroying the audio path.
 *   - H10: `createPeerConnection` is kept stable; `cleanupPeer` uses a ref.
 */
export function useVoiceChat(roomId: string | null): UseVoiceChatReturn {
  const { requestMicAndCamera } = useFarcaster();
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [activePeers, setActivePeers] = useState<string[]>([]);
  const [mutedPeers, setMutedPeers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const localStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  // Politeness flag per-peer (avoid recomputing & having inconsistent values in glare code).
  const peerIsPoliteRef = useRef<Map<string, boolean>>(new Map());
  // Queue of ICE candidates that arrived before `setRemoteDescription` finished.
  const pendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  // True once we set a remote description for this peer.
  const remoteDescriptionSetRef = useRef<Map<string, boolean>>(new Map());

  // ── Cleanup a single peer ─────────────────────────────────────────────────
  const cleanupPeer = useCallback((peerId: string) => {
    const pc = peerConnectionsRef.current.get(peerId);
    if (pc) {
      try {
        pc.close();
      } catch {
        /* ignore */
      }
    }
    peerConnectionsRef.current.delete(peerId);
    peerIsPoliteRef.current.delete(peerId);
    pendingCandidatesRef.current.delete(peerId);
    remoteDescriptionSetRef.current.delete(peerId);

    const audio = audioElementsRef.current.get(peerId);
    if (audio) {
      try {
        audio.pause();
        audio.srcObject = null;
        audio.remove();
      } catch {
        /* ignore */
      }
    }
    audioElementsRef.current.delete(peerId);

    setActivePeers((prev) => prev.filter((id) => id !== peerId));
    setMutedPeers((prev) => prev.filter((id) => id !== peerId));
  }, []);

  const cleanupPeerRef = useRef(cleanupPeer);
  cleanupPeerRef.current = cleanupPeer;

  // ── Create a new RTCPeerConnection for a peer ─────────────────────────────
  const createPeerConnection = useCallback(
    (peerId: string, isPolite: boolean): RTCPeerConnection => {
      const existing = peerConnectionsRef.current.get(peerId);
      if (existing) return existing;

      const pc = new RTCPeerConnection({ iceServers: CONFIG.ICE_SERVERS });
      peerIsPoliteRef.current.set(peerId, isPolite);

      pc.onicecandidate = ({ candidate }) => {
        if (candidate) {
          socket.emit("webrtc-ice-candidate", {
            targetId: peerId,
            candidate: candidate.toJSON(),
          });
        }
      };

      pc.onnegotiationneeded = async () => {
        try {
          // Explicit offer creation — `setLocalDescription()` without arguments
          // is unsupported on older Safari builds.
          const offer = await pc.createOffer();
          // Another offer may have arrived concurrently — re-check state.
          if (pc.signalingState !== "stable") return;
          await pc.setLocalDescription(offer);
          socket.emit("webrtc-offer", {
            targetId: peerId,
            offer: pc.localDescription,
          });
        } catch (err) {
          if (process.env.NODE_ENV !== "production") {
            console.error("[webrtc] negotiation error:", err);
          }
        }
      };

      pc.ontrack = ({ streams }) => {
        let audio = audioElementsRef.current.get(peerId);
        if (!audio) {
          audio = new Audio();
          audio.autoplay = true;
          audio.style.display = "none";
          document.body.appendChild(audio);
          audioElementsRef.current.set(peerId, audio);
        }
        audio.srcObject = streams[0];
        setActivePeers((prev) =>
          prev.includes(peerId) ? prev : [...prev, peerId],
        );
      };

      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === "disconnected" ||
          pc.connectionState === "failed" ||
          pc.connectionState === "closed"
        ) {
          cleanupPeerRef.current(peerId);
        }
      };

      peerConnectionsRef.current.set(peerId, pc);
      return pc;
    },
    [],
  );

  const addLocalTracks = useCallback((pc: RTCPeerConnection) => {
    if (!localStreamRef.current) return;
    const senders = pc.getSenders();
    localStreamRef.current.getTracks().forEach((track) => {
      const alreadyAttached = senders.some((s) => s.track === track);
      if (!alreadyAttached && localStreamRef.current) {
        pc.addTrack(track, localStreamRef.current);
      }
    });
  }, []);

  // ── Flush buffered ICE candidates once a remote description is installed ──
  const flushPendingCandidates = useCallback(async (peerId: string) => {
    const pc = peerConnectionsRef.current.get(peerId);
    const queue = pendingCandidatesRef.current.get(peerId);
    if (!pc || !queue || queue.length === 0) return;
    for (const candidate of queue) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[webrtc] flush ICE error:", err);
        }
      }
    }
    pendingCandidatesRef.current.set(peerId, []);
  }, []);

  // ── Enable voice ──────────────────────────────────────────────────────────
  const enableVoice = useCallback(async () => {
    try {
      const hasPermission = await requestMicAndCamera();
      if (!hasPermission) {
        setError("Microphone access denied. Please allow microphone access.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      localStreamRef.current = stream;
      setIsVoiceEnabled(true);
      setIsMuted(false);
      setError(null);
      socket.emit("voice-ready");
    } catch (err) {
      setError("Microphone access denied. Please allow microphone access.");
      if (process.env.NODE_ENV !== "production") {
        console.error("getUserMedia error:", err);
      }
    }
  }, [requestMicAndCamera]);

  // ── Disable voice ─────────────────────────────────────────────────────────
  const disableVoice = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;

    peerConnectionsRef.current.forEach((_pc, id) => cleanupPeerRef.current(id));
    peerConnectionsRef.current.clear();
    audioElementsRef.current.clear();
    peerIsPoliteRef.current.clear();
    pendingCandidatesRef.current.clear();
    remoteDescriptionSetRef.current.clear();

    setIsVoiceEnabled(false);
    setIsMuted(false);
    setActivePeers([]);
    setMutedPeers([]);
    socket.emit("voice-muted");
  }, []);

  const toggleVoice = useCallback(() => {
    if (isVoiceEnabled) {
      disableVoice();
    } else {
      enableVoice();
    }
  }, [isVoiceEnabled, enableVoice, disableVoice]);

  // FIX H9: muting is a local-track toggle only — the peer connection stays up.
  // A separate signaling event tells peers to render a muted indicator.
  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const audioTrack = localStreamRef.current.getAudioTracks()[0];
    if (!audioTrack) return;
    const nextMuted = !isMuted;
    audioTrack.enabled = !nextMuted;
    setIsMuted(nextMuted);
    socket.emit("voice-mute-state", { muted: nextMuted });
  }, [isMuted]);

  // ── Socket signaling listeners ────────────────────────────────────────────
  useEffect(() => {
    if (!roomId) return;

    const handlePeerVoiceReady = async ({ peerId }: { peerId: string }) => {
      if (!isVoiceEnabled || !localStreamRef.current || !socket.id) return;
      const isPolite = socket.id > peerId;
      const pc = createPeerConnection(peerId, isPolite);
      addLocalTracks(pc);
    };

    const handleOffer = async ({
      from,
      offer,
    }: {
      from: string;
      offer: RTCSessionDescriptionInit;
    }) => {
      if (!isVoiceEnabled || !localStreamRef.current || !socket.id) return;

      const isPolite = socket.id > from;
      const pc =
        peerConnectionsRef.current.get(from) ||
        createPeerConnection(from, isPolite);

      // Glare detection using the STORED politeness (avoids inconsistency).
      const readyState = pc.signalingState;
      const collision =
        offer.type === "offer" &&
        (readyState !== "stable" ||
          pc.getTransceivers().some((t) => t.mid === null));

      if (!isPolite && collision) return; // impolite peer ignores colliding offer.

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        remoteDescriptionSetRef.current.set(from, true);
        await flushPendingCandidates(from);

        if (readyState === "stable") addLocalTracks(pc);

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("webrtc-answer", { targetId: from, answer });
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.error("[webrtc] handleOffer error:", err);
        }
      }
    };

    const handleAnswer = async ({
      from,
      answer,
    }: {
      from: string;
      answer: RTCSessionDescriptionInit;
    }) => {
      const pc = peerConnectionsRef.current.get(from);
      if (!pc) return;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        remoteDescriptionSetRef.current.set(from, true);
        await flushPendingCandidates(from);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.error("[webrtc] handleAnswer error:", err);
        }
      }
    };

    // FIX H7: queue ICE candidates that arrive before the remote description.
    const handleIceCandidate = async ({
      from,
      candidate,
    }: {
      from: string;
      candidate: RTCIceCandidateInit;
    }) => {
      const pc = peerConnectionsRef.current.get(from);
      if (!pc) {
        // Peer connection doesn't exist yet — buffer.
        const queue = pendingCandidatesRef.current.get(from) ?? [];
        queue.push(candidate);
        pendingCandidatesRef.current.set(from, queue);
        return;
      }
      if (!remoteDescriptionSetRef.current.get(from)) {
        const queue = pendingCandidatesRef.current.get(from) ?? [];
        queue.push(candidate);
        pendingCandidatesRef.current.set(from, queue);
        return;
      }
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[webrtc] addIceCandidate error:", err);
        }
      }
    };

    const handlePeerVoiceMuted = ({ peerId }: { peerId: string }) => {
      // This event means the peer fully disabled voice — tear down.
      cleanupPeerRef.current(peerId);
    };

    // FIX H9: soft-mute indicator — peer connection stays up.
    const handlePeerMuteState = ({
      peerId,
      muted,
    }: {
      peerId: string;
      muted: boolean;
    }) => {
      setMutedPeers((prev) => {
        if (muted) return prev.includes(peerId) ? prev : [...prev, peerId];
        return prev.filter((id) => id !== peerId);
      });
    };

    // FIX H8: when the peer's PLAYER socket disconnects, their audio is gone
    // — tear down the WebRTC PC immediately instead of waiting for ICE timeout.
    const handlePlayerDisconnected = (data: { socketId: string }) => {
      if (!data?.socketId) return;
      if (peerConnectionsRef.current.has(data.socketId)) {
        cleanupPeerRef.current(data.socketId);
      }
    };

    socket.on("peer-voice-ready", handlePeerVoiceReady);
    socket.on("webrtc-offer", handleOffer);
    socket.on("webrtc-answer", handleAnswer);
    socket.on("webrtc-ice-candidate", handleIceCandidate);
    socket.on("peer-voice-muted", handlePeerVoiceMuted);
    socket.on("peer-voice-mute-state", handlePeerMuteState);
    socket.on("player-disconnected", handlePlayerDisconnected);

    return () => {
      socket.off("peer-voice-ready", handlePeerVoiceReady);
      socket.off("webrtc-offer", handleOffer);
      socket.off("webrtc-answer", handleAnswer);
      socket.off("webrtc-ice-candidate", handleIceCandidate);
      socket.off("peer-voice-muted", handlePeerVoiceMuted);
      socket.off("peer-voice-mute-state", handlePeerMuteState);
      socket.off("player-disconnected", handlePlayerDisconnected);
    };
  }, [roomId, isVoiceEnabled, createPeerConnection, addLocalTracks, flushPendingCandidates]);

  useEffect(() => {
    return () => {
      disableVoice();
    };
  }, [disableVoice]);

  return {
    isVoiceEnabled,
    isMuted,
    activePeers,
    mutedPeers,
    toggleVoice,
    toggleMute,
    error,
  };
}
